import React, { useState, useEffect, useRef } from 'react';
import { TrashIcon, PlusIcon, ChevronDownIcon, ChevronUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Icon, MandatoryToggle, MoveQuestionsBar, NEW_MODULE_KEY, countMandatory, fitPick, moveQuestions } from '../../components/ai/RubricModulesEditor';

export const SpinnerIcon = ({ size = 16, className = '' }) => (
  <svg
    className={`animate-spin ${className}`}
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

// Helper to generate IDs
const mkId = () => Math.random().toString(36).substring(2, 9);

/**
 * Give every module an `id` and every question a `_localId`.
 * Both are the identities the editor keys off — the module id also becomes the
 * `moduleId` each question is grouped by on save, and the question id is what
 * "move to another module" selects on — so neither may be missing.
 */
function normalizeModules(rawModules) {
  return (rawModules || []).map((m) => ({
    ...m,
    id: m.id || mkId(),
    name: m.name || m.title || 'Module',
    questionBank: (m.questionBank || m.questions || []).map((q) => (
      q && q._localId ? q : { ...q, _localId: mkId() }
    )),
  }));
}

function QuestionCard({ question, questionIndex, onUpdate, onRemove, isExpanded, onToggle, viewOnly, selectable = false, selected = false, onSelectToggle, onMandatoryChange }) {
  const addOption = () => {
    onUpdate({ options: [...(question.options || []), { text: '', isCorrect: false }] });
  };
  const updateOption = (idx, patch) => {
    const newOpts = [...(question.options || [])];
    newOpts[idx] = { ...newOpts[idx], ...patch };
    onUpdate({ options: newOpts });
  };
  const removeOption = (idx) => {
    const newOpts = [...(question.options || [])];
    newOpts.splice(idx, 1);
    onUpdate({ options: newOpts });
  };
  const setCorrectOption = (idx) => {
    const newOpts = (question.options || []).map((o, i) => ({ ...o, isCorrect: i === idx }));
    onUpdate({ options: newOpts });
  };

  return (
    <div className={`bg-white border rounded-lg shadow-sm overflow-hidden mb-2 ${selected ? 'border-blue-400 ring-1 ring-blue-200' : 'border-gray-200'}`}>
      <div
        className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {selectable && (
            <input
              type="checkbox"
              checked={selected}
              onClick={(e) => e.stopPropagation()}
              onChange={() => onSelectToggle && onSelectToggle()}
              aria-label={`Select question ${questionIndex + 1}`}
              className="flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-400 cursor-pointer"
            />
          )}
          <span className="text-xs font-semibold text-gray-500">Q{questionIndex + 1}</span>
          <span className="text-sm font-medium text-gray-700 truncate">{question.prompt || 'New question...'}</span>
        </div>
        <div className="flex items-center gap-2">
          {viewOnly
            ? question.mandatory === true && <span className="text-xs font-medium text-blue-700">Mandatory</span>
            : onMandatoryChange && <MandatoryToggle checked={question.mandatory === true} onChange={onMandatoryChange} />}
          {!viewOnly && (
            <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(); }} title="Remove question" className="text-gray-400 hover:text-red-500 p-1">
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
          {isExpanded ? <ChevronUpIcon className="w-4 h-4 text-gray-400" /> : <ChevronDownIcon className="w-4 h-4 text-gray-400" />}
        </div>
      </div>
      {isExpanded && (
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Question text *</label>
            <textarea
              rows={2}
              value={question.prompt}
              onChange={(e) => onUpdate({ prompt: e.target.value })}
              readOnly={viewOnly}
              className={`w-full text-sm border border-gray-300 rounded-md px-3 py-2 ${viewOnly ? 'bg-gray-50' : ''}`}
              placeholder="Type your question..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
              <select
                value={question.type}
                onChange={(e) => onUpdate({ type: e.target.value, options: e.target.value === 'multiple-choice' ? [{text: '', isCorrect: true}, {text: '', isCorrect: false}] : [] })}
                disabled={viewOnly}
                className={`w-full text-sm border border-gray-300 rounded-md px-2 py-2 ${viewOnly ? 'bg-gray-50' : ''}`}
              >
                <option value="short-answer">Short Answer</option>
                <option value="multiple-choice">Multiple Choice</option>
                <option value="true-false">True/False</option>
                <option value="fill-in-blanks">Fill In The Blanks</option>
                <option value="essay">Essay</option>
                <option value="code">Code</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Points</label>
              <input
                type="number"
                min="1"
                value={question.points}
                onChange={(e) => onUpdate({ points: parseInt(e.target.value) || 1 })}
                readOnly={viewOnly}
                className={`w-full text-sm border border-gray-300 rounded-md px-2 py-2 ${viewOnly ? 'bg-gray-50' : ''}`}
              />
            </div>
          </div>
          {['short-answer', 'fill-in-blanks', 'essay', 'code'].includes(question.type) && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Correct Answer (Optional)</label>
              <input
                type="text"
                value={question.correctAnswer || ''}
                onChange={(e) => onUpdate({ correctAnswer: e.target.value })}
                readOnly={viewOnly}
                className={`w-full text-sm border border-gray-300 rounded-md px-2 py-2 ${viewOnly ? 'bg-gray-50' : ''}`}
                placeholder="Expected exact string match..."
              />
            </div>
          )}
          {question.type === 'true-false' && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Correct Answer (Optional)</label>
              <select
                value={question.correctAnswer || ''}
                onChange={(e) => onUpdate({ correctAnswer: e.target.value })}
                disabled={viewOnly}
                className={`w-full text-sm border border-gray-300 rounded-md px-2 py-2 ${viewOnly ? 'bg-gray-50' : 'bg-white'}`}
              >
                <option value="">Select answer (True or False)...</option>
                <option value="true">True</option>
                <option value="false">False</option>
              </select>
            </div>
          )}
          {question.type === 'multiple-choice' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-gray-600">Options</label>
                {!viewOnly && (
                  <button type="button" onClick={addOption} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                    <PlusIcon className="w-3 h-3" /> Add Option
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {(question.options || []).map((opt, oIdx) => (
                  <div key={oIdx} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={`correct-${question._localId}`}
                      checked={opt.isCorrect}
                      onChange={() => !viewOnly && setCorrectOption(oIdx)}
                      disabled={viewOnly}
                      className="w-4 h-4 text-blue-600"
                    />
                    <input
                      type="text"
                      value={opt.text}
                      onChange={(e) => onUpdate && updateOption(oIdx, { text: e.target.value })}
                      readOnly={viewOnly}
                      placeholder={`Option ${oIdx + 1}`}
                      className={`flex-1 text-sm border border-gray-300 rounded-md px-2 py-1.5 ${viewOnly ? 'bg-gray-50' : ''}`}
                    />
                    {!viewOnly && (
                      <button type="button" onClick={() => removeOption(oIdx)} className="text-gray-400 hover:text-red-500 p-1">
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ModuleCard({ module, moduleIndex, onUpdate, onRemove, isExpanded, onToggle, title, viewOnly, totalModules = 1, siblingModules = [], onMoveQuestions }) {
  const [expandedQuestionId, setExpandedQuestionId] = useState(null);
  const [parsing, setParsing] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const handleFileParse = async (file) => {
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append('questionsFile', file);
      fd.append('moduleName', module.name);
      fd.append('constraints', JSON.stringify({ title: title || '' }));
      
      const res = await axios.post('/api/assessments/parse-assessment-module-questions', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      const jobId = res.data?.jobId;
      if (!jobId) throw new Error('Background job failed to start.');
      
      let finalResult = null;
      while (true) {
        await new Promise(r => setTimeout(r, 2000));
        const statusRes = await axios.get(`/api/assessments/parse-status/${jobId}`);
        const job = statusRes.data?.job;
        if (job?.status === 'completed') {
          finalResult = job.result;
          break;
        } else if (job?.status === 'failed') {
          throw new Error(job.error || 'Parsing failed');
        }
      }
      
      const extractedQuestions = finalResult?.questions;
      if (!Array.isArray(extractedQuestions) || extractedQuestions.length === 0) {
        toast.error('No questions could be extracted from the file for this module.');
        return;
      }
      
      const newQuestions = extractedQuestions.map((q) => ({
        _localId: mkId(),
        prompt: q.prompt || '',
        type: q.type === 'mcq' ? 'multiple-choice' : (q.type === 'true_false' ? 'true-false' : (q.type === 'fill_in_blank' ? 'fill-in-blanks' : 'short-answer')),
        options: q.type === 'mcq' && Array.isArray(q.options) 
          ? q.options.map(opt => ({ text: opt, isCorrect: opt === q.correctAnswer }))
          : q.type === 'true_false' 
            ? [{ text: 'True', isCorrect: String(q.correctAnswer).toLowerCase() === 'true' }, { text: 'False', isCorrect: String(q.correctAnswer).toLowerCase() === 'false' }]
            : [],
        correctAnswer: q.type !== 'mcq' ? (q.correctAnswer || '') : undefined,
        points: parseInt(q.points, 10) || 1,
      }));
      
      onUpdate({ questionBank: [...module.questionBank, ...newQuestions] });
      if (!isExpanded) onToggle();
      toast.success(`Added ${newQuestions.length} question(s) to ${module.name || 'this module'}.`);
    } catch (err) {
      console.error('File parse error:', err);
      const msg = err.response?.data?.message || err.message;
      if (err.response?.status === 429) {
        toast.error('AI service is busy. Please try again in a moment.');
      } else if (err.response?.status === 503) {
        toast.error('AI service is not configured on this server.');
      } else {
        toast.error(msg || 'Failed to parse the uploaded file. Please try again.');
      }
    } finally {
      setParsing(false);
    }
  };

  const addQuestion = () => {
    const newQ = {
      _localId: mkId(),
      prompt: '',
      type: 'multiple-choice',
      options: [{text: '', isCorrect: true}, {text: '', isCorrect: false}],
      points: 1,
    };
    onUpdate({ questionBank: [...module.questionBank, newQ] });
    if (!isExpanded) onToggle();
    setExpandedQuestionId(newQ._localId);
  };

  const updateQuestion = (qIdx, patch) => {
    const qs = module.questionBank.map((q, i) => i === qIdx ? { ...q, ...patch } : q);
    onUpdate({ questionBank: qs });
  };
  const pickLimits = { minPick: 0 };

  const removeQuestion = (qIdx) => {
    const removed = module.questionBank[qIdx];
    const questionBank = module.questionBank.filter((_, i) => i !== qIdx);
    // Pull the pick count down with the bank so it can never read "pick 8 of 5".
    onUpdate({ questionBank, questionsToPick: fitPick(Number(module.questionsToPick) || 0, questionBank, pickLimits) });
    if (removed?._localId) setSelectedIds((prev) => prev.filter((id) => id !== removed._localId));
  };

  // Ticking raises the pick to cover every mandatory question; unticking keeps it.
  const setMandatory = (qIdx, mandatory) => {
    const questionBank = module.questionBank.map((q, i) => (i === qIdx ? { ...q, mandatory } : q));
    onUpdate({ questionBank, questionsToPick: fitPick(Number(module.questionsToPick) || 0, questionBank, pickLimits) });
  };

  const bankSize = module.questionBank.length;
  const maxPick = Math.max(1, bankSize);
  const mandatoryCount = countMandatory(module.questionBank);
  const minPick = mandatoryCount;

  /* ── Selection → "move to another module" ── */
  // Moving is only meaningful with somewhere to move to and something to leave behind.
  const canSelect = !viewOnly && typeof onMoveQuestions === 'function' && bankSize > 1;
  const defaultNewModuleName = `Module ${totalModules + 1}`;

  const toggleSelected = (id) => {
    if (!id) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleMoveSelected = (toKey, newName) => {
    onMoveQuestions(selectedIds, toKey, newName);
    setSelectedIds([]);
  };

  return (
    <div className="border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden mb-4">
      <div 
        className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 cursor-pointer"
        onClick={onToggle}
      >
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          aria-expanded={isExpanded}
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} module ${module.name || moduleIndex + 1}`}
          className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 transition-colors"
        >
          {isExpanded ? (
            <Icon d="M5 15l7-7 7 7" size={18} />
          ) : (
            <Icon d="M19 9l-7 7-7-7" size={18} />
          )}
        </button>
        <div className="flex-1 min-w-0" onClick={e => e.stopPropagation()}>
          <input
            type="text"
            value={module.name}
            onChange={(e) => onUpdate({ name: e.target.value })}
            readOnly={viewOnly}
            placeholder="Module name (e.g. Sales, Leadership, Eligibility)..."
            className={`w-full text-sm font-semibold bg-transparent border-0 focus:outline-none focus:ring-0 text-gray-800 placeholder-gray-400 p-0 ${viewOnly ? 'cursor-default' : ''}`}
          />
          <p className="text-xs text-gray-500 mt-0.5">
            {bankSize} question{bankSize !== 1 ? 's' : ''} in bank
            {mandatoryCount > 0 && <span className="text-blue-700"> · {mandatoryCount} mandatory</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
          {!viewOnly && (
            <>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept=".pdf,.doc,.docx,.txt,.csv"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    handleFileParse(e.target.files[0]);
                    e.target.value = null; // reset
                  }
                }}
              />
              <button
                type="button"
                disabled={parsing}
                onClick={() => {
                  if (!module.name.trim()) {
                    toast.error("Please enter a module name first so we know what kind of questions to extract.");
                    return;
                  }
                  fileInputRef.current?.click();
                }}
                className={`flex items-center justify-center p-2 rounded-lg transition-colors ${
                  parsing ? "text-gray-400 opacity-50" : "text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                }`}
                title="Upload document to extract questions"
              >
                {parsing ? <SpinnerIcon size={18} /> : <Icon d="M7 16a4 4 0 0 1-.88-7.903A5 5 0 1 1 15.9 6L16 6a5 5 0 0 1 1 9.9M15 13l-3-3m0 0l-3 3m3-3v12" size={18} />}
              </button>
            </>
          )}
          
          <div className="flex flex-col items-end">
            <span className="text-xs text-gray-500 mb-0.5">Pick per assessment</span>
            <div className="flex items-center gap-1.5 bg-white border border-gray-300 rounded-lg px-2 py-1 shadow-sm">
              {!viewOnly && (
                <button
                  type="button"
                  onClick={() => onUpdate({ questionsToPick: Math.max(minPick, module.questionsToPick - 1) })}
                  className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                >
                  <Icon d="M5 12h14" size={12} />
                </button>
              )}
              <input
                type="number"
                min={1}
                max={maxPick}
                value={module.questionsToPick === '' ? '' : module.questionsToPick}
                readOnly={viewOnly}
                onChange={(e) => {
                  let val = e.target.value;
                  if (val === '') {
                    onUpdate({ questionsToPick: '' });
                    return;
                  }
                  val = parseInt(val, 10);
                  if (!isNaN(val)) {
                    onUpdate({ questionsToPick: Math.min(maxPick, Math.max(minPick, val)) });
                  }
                }}
                onBlur={() => {
                  if (module.questionsToPick === '' || module.questionsToPick < minPick) {
                    onUpdate({ questionsToPick: minPick });
                  }
                }}
                className="w-8 text-center text-sm font-bold text-blue-700 bg-transparent border-none p-0 focus:ring-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                style={{ MozAppearance: 'textfield' }}
              />
              {!viewOnly && (
                <button
                  type="button"
                  onClick={() => onUpdate({ questionsToPick: Math.min(maxPick, module.questionsToPick + 1) })}
                  className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                >
                  <Icon d="M12 5v14M5 12h14" size={12} />
                </button>
              )}
            </div>
          </div>
        </div>
        {!viewOnly && (
          <button
            type="button"
            onClick={onRemove}
            className="flex-shrink-0 ml-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Remove module"
          >
            <Icon d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" size={16} />
          </button>
        )}
      </div>
      {isExpanded && (
        <div className="p-4 space-y-3">
          {/* Duration removed for Rubric-Based Assessment */}

          {module.questionBank.length === 0 && (
            <div className="text-center py-6 text-gray-400">
              <Icon d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" size={28} className="mx-auto mb-2" />
              <p className="text-sm">No questions yet. Add some below.</p>
            </div>
          )}
          {selectedIds.length > 0 && (
            <MoveQuestionsBar
              count={selectedIds.length}
              siblingModules={siblingModules}
              defaultNewName={defaultNewModuleName}
              canMove={selectedIds.length < bankSize}
              blockedReason="Keep at least one question here — rename this module instead of emptying it."
              onClear={() => setSelectedIds([])}
              onMove={handleMoveSelected}
            />
          )}
          {module.questionBank.map((q, idx) => (
            <QuestionCard
              key={q._localId}
              question={q}
              questionIndex={idx}
              onUpdate={(patch) => updateQuestion(idx, patch)}
              onRemove={() => removeQuestion(idx)}
              isExpanded={expandedQuestionId === q._localId}
              onToggle={() => setExpandedQuestionId(prev => prev === q._localId ? null : q._localId)}
              viewOnly={viewOnly}
              selectable={canSelect && Boolean(q._localId)}
              selected={selectedIds.includes(q._localId)}
              onSelectToggle={() => toggleSelected(q._localId)}
              onMandatoryChange={(checked) => setMandatory(idx, checked)}
            />
          ))}
          {!viewOnly && (
            <button
              type="button"
              onClick={addQuestion}
              className="w-full flex items-center justify-center gap-1.5 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition-all"
            >
              <Icon d="M12 5v14M5 12h14" size={14} />
              Add question
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const assessmentCategoryOptions = [
  { value: 'sales', label: 'Sales', description: 'For SDRs, AEs, and revenue roles.' },
  { value: 'marketing', label: 'Marketing', description: 'For growth, content, and brand roles.' },
  { value: 'software-engineering', label: 'Software Engineering', description: 'For backend, frontend, and full-stack roles.' },
  { value: 'data', label: 'Data & Analytics', description: 'For data analysts, scientists, and BI roles.' },
  { value: 'product', label: 'Product Management', description: 'For PMs and product strategy roles.' },
  { value: 'recruiting', label: 'Talent & Recruiting', description: 'For recruiters and talent partners.' },
  { value: 'customer-success', label: 'Customer Success', description: 'For CS, onboarding, and account roles.' },
  { value: 'operations', label: 'Operations', description: 'For business, ops, and enablement roles.' },
  { value: 'finance', label: 'Finance', description: 'For FP&A, accounting, and finance roles.' },
  { value: 'people', label: 'People & HR', description: 'For HRBPs and people operations roles.' },
  { value: 'it', label: 'IT', description: 'For IT support, systems, and infrastructure roles.' },
  { value: 'engineering', label: 'Engineering', description: 'For engineering roles across disciplines.' },
  { value: 'general', label: 'General', description: 'A broad evaluation for mixed roles.' },
  { value: 'communication', label: 'Communication', description: 'For roles requiring strong verbal and written communication skills.' },
  { value: 'customer-support', label: 'Customer Support', description: 'For technical support, customer service, and helpdesk.' },
  { value: 'design-ux', label: 'Design & UX', description: 'For UX/UI designers, graphic designers, and researchers.' },
  { value: 'accounting', label: 'Accounting', description: 'For accountants, bookkeepers, and tax professionals.' },
  { value: 'legal', label: 'Legal', description: 'For legal counsel, paralegals, and compliance officers.' },
  { value: 'healthcare', label: 'Healthcare', description: 'For nurses, medical assistants, and healthcare administrators.' },
  { value: 'education', label: 'Education & Training', description: 'For teachers, trainers, and instructional designers.' },
  { value: 'administration', label: 'Administration', description: 'For administrative assistants, office managers, and receptionists.' },
  { value: 'project-management', label: 'Project Management', description: 'For Scrum masters, Agile coaches, and project coordinators.' },
  { value: 'business-development', label: 'Business Development', description: 'For partnerships, alliances, and B2B growth roles.' },
  { value: 'supply-chain', label: 'Supply Chain & Logistics', description: 'For procurement, inventory, and logistics managers.' },
  { value: 'quality-assurance', label: 'Quality Assurance', description: 'For manual/automation testers and QA engineers.' },
  { value: 'cybersecurity', label: 'Cybersecurity', description: 'For security analysts, penetration testers, and compliance.' },
];


function ModuleGroupCard({ groupName, modulesWithIndex, title, expandedModuleId, setExpandedModuleId, updateModule, removeModule }) {
  const [isGroupExpanded, setIsGroupExpanded] = React.useState(true);
  
  const totalQuestionsInBank = modulesWithIndex.reduce((sum, {m}) => sum + (m.questionBank?.length || 0), 0);
  const totalQuestionsToPick = modulesWithIndex.reduce((sum, {m}) => sum + (m.questionsToPick || 0), 0);
  const totalDuration = modulesWithIndex.reduce((sum, {m}) => sum + (m.duration || 0), 0);

  return (
    <div className="border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden mb-4">
      <div 
        className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 border-b border-gray-200 cursor-pointer hover:bg-gray-50/80 transition-colors"
        onClick={() => setIsGroupExpanded(!isGroupExpanded)}
      >
        <div className="flex-shrink-0 p-1 text-gray-400 transition-colors">
          {isGroupExpanded ? (
            <Icon d="M5 15l7-7 7 7" size={18} />
          ) : (
            <Icon d="M19 9l-7 7-7-7" size={18} />
          )}
        </div>
        <div className="flex-1 min-w-0" onClick={e => e.stopPropagation()}>
          <div className="w-full text-sm font-semibold text-gray-800 p-0">
            {groupName} <span className="text-xs font-normal text-gray-500 ml-1">({modulesWithIndex.length} parts)</span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {totalQuestionsInBank} questions in bank
          </p>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0" onClick={e => e.stopPropagation()}>
           <div className="text-xs text-gray-500 text-right">
             <div className="font-medium text-gray-700">{totalQuestionsToPick} total picks</div>
             <div>{totalDuration} mins</div>
           </div>
        </div>
      </div>

      {isGroupExpanded && (
        <div className="p-3 bg-gray-50/30 space-y-3 border-t border-gray-100">
          {modulesWithIndex.map(({ m, idx }) => (
            <div key={m.id} className="pl-6 border-l-2 border-blue-200/50 ml-2">
              <ModuleCard
                title={title}
                module={m}
                moduleIndex={idx}
                onUpdate={(patch) => updateModule(idx, patch)}
                onRemove={() => removeModule(idx)}
                isExpanded={expandedModuleId === m.id}
                onToggle={() => setExpandedModuleId(prev => prev === m.id ? null : m.id)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const MultiModuleCreate = ({
  screeningName,
  registerSubmit,
  onLoadingChange,
  onValidationChange,
  onDraftChange,
  viewOnly = false,
  modules: initialModules = [],
  initialDraft,
}) => {
  const [title, setTitle] = useState(initialDraft?.title || (screeningName ? `${screeningName} Multi-Module Assessment` : ''));
  const [category, setCategory] = useState(initialDraft?.category || '');
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [globalDuration, setGlobalDuration] = useState(initialDraft?.globalDuration || initialDraft?.estimatedTime || initialDraft?.assessmentDuration || initialDraft?.duration || 30);
  const [modules, setModules] = useState(() => {
    const rawMods = initialDraft?.modules?.length > 0 ? initialDraft.modules : initialModules;
    if (rawMods?.length > 0) {
      return normalizeModules(rawMods);
    }
    return [{
      id: mkId(),
      name: 'Module 1',
      questionsToPick: 1,
      questionBank: [],
    }];
  });

  useEffect(() => {
    if (viewOnly && initialDraft) {
      if (initialDraft.title) setTitle(initialDraft.title);
      if (initialDraft.category) setCategory(initialDraft.category);
      if (initialDraft.modules?.length > 0) {
        setModules(normalizeModules(initialDraft.modules));
      }
    }
  }, [initialDraft, viewOnly]);

  const [expandedModuleId, setExpandedModuleId] = useState(modules[0]?.id || null);

  const isValid = title.trim().length > 0 && category && modules.length > 0 && modules.every(m => m.name.trim().length > 0 && m.questionBank.length > 0);

  useEffect(() => {
    if (!viewOnly) {
      onValidationChange?.(isValid);
    }
  }, [isValid, onValidationChange, viewOnly]);

  useEffect(() => {
    if (!viewOnly) {
      onDraftChange?.({ title, category, modules, globalDuration });
    }
  }, [title, category, modules, globalDuration, onDraftChange, viewOnly]);

  const globalFileInputRef = useRef(null);
  const [globalParsing, setGlobalParsing] = useState(false);

  const handleGlobalFileParse = async (file) => {
    if (!file) return;
    setGlobalParsing(true);
    const formData = new FormData();
    formData.append('questionsFile', file);
    formData.append('constraints', JSON.stringify({ title }));

    try {
      const res = await axios.post('/api/assessments/parse-assessment-full-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      const jobId = res.data?.jobId;
      if (!jobId) throw new Error('Background job failed to start.');
      
      let finalResult = null;
      while (true) {
        await new Promise(r => setTimeout(r, 2000));
        const statusRes = await axios.get(`/api/assessments/parse-status/${jobId}`);
        const job = statusRes.data?.job;
        if (job?.status === 'completed') {
          finalResult = job.result;
          break;
        } else if (job?.status === 'failed') {
          throw new Error(job.error || 'Parsing failed');
        }
      }

      if (finalResult && finalResult.modules) {
        const newModules = finalResult.modules.map(m => ({
          id: mkId(),
          name: m.name || m.title || 'Parsed Module',
          duration: m.duration || 10,
          questionsToPick: m.questionsToPick !== undefined && m.questionsToPick !== null ? Number(m.questionsToPick) : 1,
          questionBank: (m.questions || []).map(q => ({
            ...q,
            options: q.type === 'multiple-choice' && Array.isArray(q.options) 
              ? q.options.map(opt => ({ text: opt, isCorrect: opt === q.correctAnswer }))
              : q.type === 'true-false'
                ? [{ text: 'True', isCorrect: String(q.correctAnswer).toLowerCase() === 'true' }, { text: 'False', isCorrect: String(q.correctAnswer).toLowerCase() === 'false' }]
                : [],
            _localId: mkId(),
          }))
        }));
        
        // Either append or replace if only default empty module exists
        if (modules.length === 1 && modules[0].questionBank.length === 0 && modules[0].name === 'Module 1') {
          setModules(newModules);
        } else {
          setModules([...modules, ...newModules]);
        }
        
        if (newModules.length > 0) {
          setExpandedModuleId(newModules[0].id);
        }
        toast.success(`Successfully extracted ${newModules.length} modules!`);
      } else {
        toast.error('Failed to parse document. No modules returned.');
      }
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.message || error.message || 'Error extracting modules from file';
      toast.error(msg);
    } finally {
      setGlobalParsing(false);
    }
  };

  const addModule = () => {
    const newMod = {
      id: mkId(),
      name: `Module ${modules.length + 1}`,
      questionsToPick: 1,
      questionBank: [],
    };
    setModules([...modules, newMod]);
    setExpandedModuleId(newMod.id);
  };

  const updateModule = (mIdx, patch) => {
    const updated = [...modules];
    updated[mIdx] = { ...updated[mIdx], ...patch };
    setModules(updated);
  };

  const removeModule = (mIdx) => {
    setModules(modules.filter((_, i) => i !== mIdx));
  };

  // Re-parent questions out of module `fromIdx`. One atomic update — updateModule
  // above reads a stale `modules` closure, so two calls would clobber each other.
  const handleMoveQuestions = (fromIdx, questionIds, toKey, newModuleName) => {
    const newModuleId = toKey === NEW_MODULE_KEY ? mkId() : null;
    setModules((prev) =>
      moveQuestions(prev, {
        fromIdx,
        toKey,
        questionIds,
        idKey: 'id',
        pickKey: 'questionsToPick',
        minPick: 0,
        newModuleName,
        newModuleId,
      })
    );
    if (newModuleId) setExpandedModuleId(newModuleId);
  };

  useEffect(() => {
    if (registerSubmit) {
      registerSubmit(async () => {
        if (!isValid) return false;
        onLoadingChange?.(true);

        const payload = {
          title: title.trim(),
          category: category,
          isMultiModule: false,
          isRubricBased: true,
          modules: modules.map((m, index) => ({
            id: m.id,
            order: index + 1,
            title: m.name,
            name: m.name,
            questionsToPick: m.questionsToPick !== undefined && m.questionsToPick !== null ? Number(m.questionsToPick) : 1,
            questionBank: m.questionBank || [],
          })),
          estimatedTime: globalDuration,
          duration: globalDuration,
          assessmentDuration: globalDuration,
        };

        const flattenedQuestions = modules.flatMap(m => 
          m.questionBank.map((q) => ({
            title: q.prompt || q.title || q.content || q.text || '',
            content: q.prompt || q.content || q.title || q.text || '',
            type: q.type,
            options: q.options,
            correctAnswer: q.type === 'short-answer' || q.type === 'fill-in-blanks' || q.type === 'true-false' || q.type === 'essay' || q.type === 'code' ? q.correctAnswer : null,
            points: q.points,
            mandatory: q.mandatory === true,
            moduleId: m.id,
          }))
        );

        return { finish: true, formData: payload, questions: flattenedQuestions };
      });
    }
  }, [registerSubmit, isValid, title, category, modules, globalDuration, onLoadingChange]);

  const totalQuestions = modules.reduce((sum, m) => sum + (m.questionsToPick !== undefined && m.questionsToPick !== null ? Number(m.questionsToPick) : 1), 0);

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        {/* Title */}
        <div className="flex flex-col">
          <label className="block text-[15px] font-semibold text-gray-900 mb-1">
            Assessment title <span className="text-red-500">*</span>
          </label>
          <p className="text-[13px] text-gray-500 mb-3">
            Give your assessment a clear name to help you identify it later.
          </p>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={100}
            placeholder="e.g. Full Stack Engineer Comprehensive Test"
            readOnly={viewOnly}
            className={`w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF] ${viewOnly ? 'bg-gray-50 text-gray-700 cursor-not-allowed' : ''}`}
          />
          <p className="text-[11px] text-gray-400 mt-1.5">
            {title.length}/100 characters
          </p>
        </div>

        {/* Category */}
        <div className="relative flex flex-col">
          <label className="block text-[15px] font-semibold text-gray-900 mb-1">
            Category <span className="text-red-500">*</span>
          </label>
          <p className="text-[13px] text-gray-500 mb-3">
            Choose the closest role family so we can tailor questions to that track.
          </p>
          <button
            type="button"
            onClick={() => !viewOnly && setIsCategoryOpen((open) => !open)}
            className={`flex w-full items-center justify-between rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#007BFF]/30 ${
              viewOnly ? 'bg-gray-50 text-gray-700 cursor-not-allowed' : 'bg-white hover:border-[#007BFF]/60 hover:bg-gray-50'
            }`}
          >
            <span className={category ? '' : 'text-gray-400'}>
              {category
                ? assessmentCategoryOptions.find((c) => c.value === category)?.label || 'Select category'
                : 'Select category'}
            </span>
            <span className={`ml-2 transform text-gray-400 transition-transform duration-150 ${isCategoryOpen ? 'rotate-180' : 'rotate-0'}`}>
              ▾
            </span>
          </button>
          {isCategoryOpen && !viewOnly && (
            <div className="absolute z-20 top-[calc(100%+0.25rem)] w-full origin-top rounded-lg border border-gray-200 bg-white shadow-lg ring-1 ring-black/5 transition-all duration-150 ease-out">
            <div className="max-h-64 overflow-y-auto py-1">
              {assessmentCategoryOptions.map((option) => {
                const selected = category === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setCategory(prev => prev === option.value ? '' : option.value);
                      setIsCategoryOpen(false);
                    }}
                    className={`flex w-full flex-col items-start px-3 py-2 text-left text-sm transition-colors ${
                      selected ? 'bg-[#007BFF]/5 text-gray-900' : 'hover:bg-gray-50 text-gray-800'
                    }`}
                  >
                    <span className="font-medium">{option.label}</span>
                    <span className="mt-0.5 text-xs text-gray-500">{option.description}</span>
                  </button>
                );
              })}
            </div>
            </div>
          )}
        </div>
      </div>

      <div>
        <div className="flex flex-col mb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-[15px] font-semibold text-gray-900">Question Modules</h3>
              <p className="text-[13px] text-gray-500 mt-1">Build modules manually.</p>
            </div>
            <div className="flex items-center gap-3">
              {!viewOnly && (
                <>
                  {/* Global upload button commented out per request
                  {!viewOnly && (
                    <>
                      <input
                        type="file"
                        ref={globalFileInputRef}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.txt,.csv"
                        onChange={(e) => {
                          if (e.target.files && e.target.files.length > 0) {
                            handleGlobalFileParse(e.target.files[0]);
                            e.target.value = null; // reset
                          }
                        }}
                      />
                      <button
                        type="button"
                        disabled={globalParsing}
                        onClick={() => globalFileInputRef.current?.click()}
                        className={`flex items-center flex-shrink-0 whitespace-nowrap gap-1.5 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium transition-colors shadow-sm ${globalParsing ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50 border-blue-200'}`}
                      >
                        {globalParsing ? <SpinnerIcon size={16} /> : <Icon d="M7 16a4 4 0 0 1-.88-7.903A5 5 0 1 1 15.9 6L16 6a5 5 0 0 1 1 9.9M15 13l-3-3m0 0l-3 3m3-3v12" size={16} />}
                        {globalParsing ? 'Parsing...' : 'Upload File'}
                      </button>
                    </>
                  )}
                  */}
                  <button
                    type="button"
                    onClick={addModule}
                    className="flex items-center flex-shrink-0 whitespace-nowrap gap-1.5 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                  >
                    <Icon d="M12 5v14M5 12h14" size={16} /> Add Module
                  </button>
                </>
              )}
            </div>
          </div>
          {/* Global Settings */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 bg-white border border-gray-200 rounded-lg mb-3 shadow-sm">
            <div className="flex flex-col mb-2 sm:mb-0">
              <span className="text-[11px] text-gray-500 font-medium uppercase tracking-wider">Total Picked Questions</span>
              <span className="text-base font-semibold text-gray-900">{totalQuestions}</span>
            </div>
            <div className="flex flex-col items-start sm:items-end">
              <label className="text-[11px] text-gray-500 font-medium uppercase tracking-wider mb-0.5">Total Duration (min)</label>
              <input
                type="number"
                min="1"
                value={globalDuration}
                onChange={(e) => setGlobalDuration(parseInt(e.target.value) || 1)}
                readOnly={viewOnly}
                className={`w-20 text-center text-sm font-semibold border border-gray-300 rounded-md py-0.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${viewOnly ? 'bg-gray-50 text-gray-600 cursor-not-allowed' : 'text-gray-900 bg-white'}`}
              />
            </div>
          </div>

          {/* How it works banner */}
          <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl mb-6">
            <Icon d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-0.5">How it works</p>
              <p className="text-xs leading-relaxed">
                Each module is a question bank. For every candidate, the system randomly picks the configured number of questions from each module and gives that flat list to the candidate to evaluate. Candidates always get a fresh, different set. Questions marked Mandatory are given to every candidate; the rest of the pick is random.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {modules.map((m, idx) => (
            <ModuleCard
              key={m.id}
              title={title}
              module={m}
              moduleIndex={idx}
              onUpdate={(patch) => updateModule(idx, patch)}
              onRemove={() => removeModule(idx)}
              isExpanded={expandedModuleId === m.id}
              onToggle={() => setExpandedModuleId(prev => prev === m.id ? null : m.id)}
              viewOnly={viewOnly}
              totalModules={modules.length}
              siblingModules={modules
                .filter((s) => s.id !== m.id)
                .map((s) => ({ key: s.id, name: s.name }))}
              onMoveQuestions={(ids, toKey, newName) => handleMoveQuestions(idx, ids, toKey, newName)}
            />
          ))}
          {modules.length === 0 && (
            <div className="text-center py-12 text-sm text-gray-500 bg-gray-50 border border-gray-200 border-dashed rounded-xl">
              No modules added yet. Add a module to get started.
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default MultiModuleCreate;
